import { shiftService } from "@/services/ShiftService";
import { analyticsService } from "@/services/AnalyticsService";
import { toast } from "vue3-toastify";
import {i18n} from "@/lang";
import {downloadCsvBlob} from "@/helpers/csv";

const DEFAULT_START_OFFSET = 7 * 24 * 60 * 60 * 1000;
const DEFAULT_END_OFFSET = 2 * 24 * 60 * 60 * 1000;

function shiftRange(state, shiftId) {
    const cached = String(state.shiftIdDataKey) === String(shiftId) ? state.shiftIdData?.shift : null;
    const source = cached ? Promise.resolve(cached) : shiftService.getShiftIdService(shiftId).then((response) => response.data?.shift);
    return source.then((shift) => {
        if (!shift?.startTimestamp) return Promise.reject(i18n.global.t("toast.downloadError"));
        return { startDate: shift.startTimestamp, endDate: shift.endTimestamp || Date.now() };
    });
}

function downloadUsersReport(commit, state, resolveRange) {
    if (state.downloadUsersReportRequest.loading) return Promise.resolve(false);
    commit("setDownloadUsersReportRequest", {loading: true});
    return resolveRange()
      .then(({ startDate, endDate }) => shiftService.getShiftUsersReportDownloadService(startDate, endDate))
      .then((blob) => {
        downloadCsvBlob(blob, "users_report.csv");
        commit("setDownloadUsersReportRequest", {loading: false});
        return true;
      })
      .catch((error) => {
        toast.error(error || i18n.global.t("toast.downloadError"));
        commit("setDownloadUsersReportRequest", {loading: false, message: error});
        return false;
      });
}

let shiftListRequestId = 0;
let shiftListPending = false;
let shiftCurrentRequestId = 0;
let shiftDetailRequestId = 0;

const state = {
    shiftData: {},
    shiftIdData: {},
    shiftIdDataKey: null,
    shiftDataX: {},
    shiftMessage: {},
    shiftCurrentData: {},
    updateShiftLatestData: {},
    isLoadingShiftRequest: {
      loading: false
    },
    getShiftDataRequest: {
      loading: false
    },
    downloadUsersReportRequest: {
      loading: false
    },
    queryStartDateTime: Date.now() - DEFAULT_START_OFFSET,
    queryEndDateTime: Date.now() + DEFAULT_END_OFFSET,
};

const actions = {
    async getShiftDataAndCurrentShift({ commit, state }) {
        const requestId = ++shiftListRequestId;
        const currentRequestId = ++shiftCurrentRequestId;
        shiftListPending = true;
        commit("setLoadingRequest", true);
        commit("setShiftDataRequest", {loading: true});

        try {
            const [shiftDataRes, currentShiftRes] = await Promise.all([
                shiftService.getShiftService(state.queryStartDateTime, state.queryEndDateTime),
                shiftService.getShiftCurrentService()
            ]);
            if (currentRequestId === shiftCurrentRequestId) commit("updateShiftCurrentData", currentShiftRes.data);
            if (requestId !== shiftListRequestId) return;

            commit("updateShiftData", shiftDataRes.data);
            commit("setShiftDataRequest", {loading: false});
        } catch (error) {
            console.error("Error :", error);
            if (requestId === shiftListRequestId) commit("setShiftDataRequest", {loading: false, message: error});
        } finally {
            if (requestId === shiftListRequestId) {
                shiftListPending = false;
                commit("setLoadingRequest", false);
            }
        }
    },
    getShiftData({ commit, state }, options = false) {
        if (shiftListPending && options?.force !== true) return;
        const requestId = ++shiftListRequestId;
        shiftListPending = true;
        commit("setShiftDataRequest", {loading: true});
        if (options === true || options?.clear === true){
            commit("updateShiftData", null);
        }
        shiftService
          .getShiftService(state.queryStartDateTime, state.queryEndDateTime)
          .then((response) => {
            if (requestId !== shiftListRequestId) return;
            shiftListPending = false;
            commit("setShiftDataRequest", {loading: false});
            commit("updateShiftData", response.data);
          })
          .catch((error) => {
            if (requestId !== shiftListRequestId) return;
            shiftListPending = false;
            commit("setShiftDataRequest", {loading: false, message: error});
          });
    },
    getShiftCurrentData({ commit, state }) {
        const currentRequestId = ++shiftCurrentRequestId;
        commit("setLoadingRequest", true);
        shiftService
            .getShiftCurrentService()
            .then((response) => {
                commit("setLoadingRequest", false);
                if (currentRequestId === shiftCurrentRequestId) commit("updateShiftCurrentData", response.data);
            })
            .catch((error) => {
                commit("setLoadingRequest", false);
            });
    },
    getShiftIdData({ commit, state }, shiftId) {
        const requestId = ++shiftDetailRequestId;
        commit("setLoadingRequest", true);
        if (String(state.shiftIdDataKey) !== String(shiftId)) {
            commit("updateShiftIdData", {});
            commit("setShiftIdDataKey", null);
        }
        shiftService
          .getShiftIdService(shiftId)
          .then((response) => {
            if (requestId !== shiftDetailRequestId) return;
            const clientAccountsWithChecked = response.data.clientAccountHistoryData?.clientAccounts?.map(row => ({
              ...row,
              checked: false,
            }));
            if (response.data.clientAccountHistoryData?.clientAccounts) {
              response.data.clientAccountHistoryData.clientAccounts = clientAccountsWithChecked;
            }
            commit("updateShiftIdData", response.data);
            commit("setShiftIdDataKey", String(shiftId));
            commit("setLoadingRequest", false);
            commit("updateShiftMessage", response.message);
          })
          .catch((error) => {
            if (requestId !== shiftDetailRequestId) return;
            commit("setLoadingRequest", false);
          });
    },
    getShiftIdDataX({ commit, state }, shiftId) {
        commit("setLoadingRequest", true);
        commit("updateShiftDataX", {});
        shiftService
          .getShiftDetailDataXService(shiftId)
          .then((response) => {
            commit("setLoadingRequest", false);
            commit("updateShiftDataX", response.data);
            commit("updateShiftMessage", response.message);
          })
          .catch((error) => {
            commit("setLoadingRequest", false);
          });
    },
    getShiftStatsDataXDate({ commit, state }, date) {
        commit("setLoadingRequest", true);
        shiftService
          .getShiftStatsDataXDateService(date?.startDate, date?.endDate)
          .then((response) => {
            commit("setLoadingRequest", false);
            commit("updateShiftDataX", response.data);
            commit("updateShiftMessage", response.message);
          })
          .catch((error) => {
            commit("setLoadingRequest", false);
          });
    },
    getShiftLatestData({ commit, state }) {
        commit("setLoadingRequest", true);
        shiftService
          .getShiftLatestService()
          .then((response) => {
            commit("setLoadingRequest", false);
            commit("updateShiftLatestData", response.data);
          })
          .catch((error) => {
            commit("setLoadingRequest", false);
          });
    },
    deleteAccountWithHistory({ dispatch, commit }, { accountId, shiftId }) {
        commit("setLoadingRequest", true);
        analyticsService
          .deleteAccount(accountId)
          .then(() => {
            dispatch("getShiftIdData", shiftId);
            toast.success(i18n.global.t("toast.deleteSuccess"));
          })
          .catch(() =>
            commit("setLoadingRequest", false)
          );
    },
    deleteAccountWithHistoryBatch({ dispatch, commit }, { accountIds, shiftId }) {
        commit("setLoadingRequest", true);
        const ids = Array.isArray(accountIds)
            ? accountIds.map(p => (typeof p === 'object' ? p.clientAccountId : p))
            : [accountIds].filter(Boolean);
        analyticsService.deleteAccountBatch(ids)
            .then(() => {
                dispatch("getShiftIdData", shiftId);
                toast.success(i18n.global.t("toast.deleteSuccess"));
            })
            .catch(() =>
                commit("setLoadingRequest", false)
            );
    },
    getShiftDetailDownloadXData({ commit, state }, shiftId) {
        commit("setLoadingRequest", true);
        shiftService
          .getShiftDetailDownloadXService(shiftId)
          .then((response) => {
            window.open(URL.createObjectURL(response));
          })
          .catch((error) => {
            commit("setLoadingRequest", false);
          });
    },
    getShiftDetailDownloadZData({ commit, state }, shiftId) {
        commit("setLoadingRequest", true);
        shiftService
          .getShiftDetailDownloadZService(shiftId)
          .then((response) => {
            window.open(URL.createObjectURL(response));
              commit("setLoadingRequest", false);
          })
          .catch((error) => {
            commit("setLoadingRequest", false);
          });
    },
    getShiftDownloadXData({ commit, state }, page) {
        commit("setLoadingRequest", true);
        shiftService
          .getShiftDownloadXService( page,
            page === 'shiftDetail' ? state?.shiftIdData?.shift?.shiftId :
            state?.queryStartDateTime,
            state?.queryEndDateTime
          )
          .then((response) => {
            page === 'shiftDetail' ?
            window.open(URL.createObjectURL(response?.data))
            :
            window.open(URL.createObjectURL(response));
              commit("setLoadingRequest", false);
          })
          .catch((error) => {
            commit("setLoadingRequest", false);
          });
    },
    downloadShiftUsersReport({ commit, state }, { startDate, endDate }) {
        return downloadUsersReport(commit, state, () => Promise.resolve({ startDate, endDate }));
    },
    downloadShiftUsersReportByShift({ commit, state }, shiftId) {
        return downloadUsersReport(commit, state, () => shiftRange(state, shiftId));
    },
    getShiftDownloadZData({ commit, state }, page) {
        commit("setLoadingRequest", true);
        shiftService
          .getShiftDownloadZService(page,
            page === 'shiftDetail' ? state?.shiftIdData?.shift?.shiftId :
            state?.queryStartDateTime,
            state?.queryEndDateTime
          )
          .then((response) => {
            page === 'shiftDetail' ?
            window.open(URL.createObjectURL(response?.data))
            :
            window.open(URL.createObjectURL(response));

            commit("setLoadingRequest", false);
          })
          .catch((error) => {
            commit("setLoadingRequest", false);
          });
    },
    async forceClosShiftForMixedPayment({ dispatch, commit }, { myComment, myCurrentCash, myPaymentTypeForForcedClose }) {
        commit("setLoadingRequest", true);
        commit("updateShiftMessage", null);
        await shiftService.forceCloseShift(myComment, myCurrentCash, myPaymentTypeForForcedClose)
            .then((response) => {
                commit("setLoadingRequest", false);
                toast.success(i18n.global.t("toast.successEdit"));
                return response
            })
            .catch((error) => {
                commit("setLoadingRequest", false)
                commit("updateShiftMessage", error);
                return false
            });
    },
    openNewShift({ commit }, { myCurrentCash }) {
        commit("setLoadingRequest", true);
        return shiftService.openNewShift(myCurrentCash)
            .then((response) => {
                commit("setLoadingRequest", false);
                toast.success(i18n.global.t("toast.createSuccess"));
                return true;
            })
            .catch((error) => {
                commit("setLoadingRequest", false);
                toast.error(i18n.global.t("toast.createError"));
                return false;
            });
    }
};

const mutations = {
    setLoadingRequest(state, isLoading) {
        state.isLoadingShiftRequest = {loading: isLoading};
    },
    setShiftDataRequest(state, {loading, message}) {
        state.getShiftDataRequest = {loading, message};
    },
    setDownloadUsersReportRequest(state, {loading, message}) {
        state.downloadUsersReportRequest = {loading, message};
    },
    updateShiftData(state, shiftData) {
        state.shiftData = shiftData;
    },
    updateShiftIdData(state, shiftIdData) {
        state.shiftIdData = shiftIdData;
    },
    setShiftIdDataKey(state, key) {
        state.shiftIdDataKey = key;
    },
    updateShiftDataX(state, shiftData) {
        state.shiftDataX = shiftData;
    },
    updateShiftMessage(state, message) {
        state.shiftMessage = message;
    },

    updateShiftCurrentData(state, shiftCurrentData) {
        state.shiftCurrentData = shiftCurrentData;
    },
    updateShiftLatestData(state, shiftLatestData) {
        state.shiftLatestData = shiftLatestData;
    },
    setQueryStartDateTime(state, startDate) {
        state.queryStartDateTime = startDate;
    },
    setQueryEndDateTime(state, endDate) {
        state.queryEndDateTime = endDate;
    },
    resetQueryDates(state) {
        state.queryStartDateTime = Date.now() - DEFAULT_START_OFFSET;
        state.queryEndDateTime = Date.now() + DEFAULT_END_OFFSET;
    }
};

export const shift = {
    namespaced: true,
    state,
    actions,
    mutations,
}